import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  connectAuthEmulator, createUserWithEmailAndPassword, getAuth, onAuthStateChanged,
  sendPasswordResetEmail, signInWithEmailAndPassword, signOut, type Auth, type User,
} from "firebase/auth";
import { config } from "../config";

// The SOLE importer of the Firebase SDK in this app — same boundary as customer-web's
// auth/firebase.ts, so tests stub one module and the SDK never leaks into components.
// Staff sign in with email + password (customer-web uses OTP-minted custom tokens): staff
// accounts are created by invite, and the invitee sets their own password (design spec §6).

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

export function getFirebaseAuth(): Auth {
  if (!auth) {
    app = initializeApp({ apiKey: config.firebase.apiKey, projectId: config.firebase.projectId });
    auth = getAuth(app);
    if (config.firebase.authEmulatorUrl) {
      connectAuthEmulator(auth, config.firebase.authEmulatorUrl, { disableWarnings: true });
    }
  }
  return auth;
}

export function subscribeAuth(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(getFirebaseAuth(), cb);
}

export const firebaseSignInWithPassword = (email: string, password: string) =>
  signInWithEmailAndPassword(getFirebaseAuth(), email, password);

export const firebaseSignOut = () => signOut(getFirebaseAuth());

// An invitee sets their own password when accepting an invite — the account is created
// here, in the browser, and the API then binds the resulting uid to the invite.
export const firebaseCreateUser = (email: string, password: string) =>
  createUserWithEmailAndPassword(getFirebaseAuth(), email, password);

export const firebaseSendPasswordReset = (email: string) =>
  sendPasswordResetEmail(getFirebaseAuth(), email);
